import { initShaderProgram, IShaderProgramInfo } from "../../../../SharedGL/Shader";
import { Camera } from "../../../../SharedGL/Camera";
import { mat4 } from "gl-matrix";
import { IRGBA } from "../../TimelineUtils";

export class SelectedDayShader {
  // Vertex shader program
  private readonly vsSource = `
    attribute vec4 aVertexPosition;

    uniform mat4 uModelViewMatrix;
    uniform mat4 uProjectionMatrix;
    uniform lowp vec4 uColor;

    varying lowp vec4 vColor;
    void main(void) {
      gl_Position = uProjectionMatrix * uModelViewMatrix * aVertexPosition;
      vColor = uColor;
    }
  `;
  // Fragment shader program
  private readonly fsSource = `
    varying lowp vec4 vColor;

    void main(void) {
      gl_FragColor = vColor ;
    }
  `;

  private shaderProgram: WebGLProgram;
  private programInfo: IShaderProgramInfo;

  constructor(gl: WebGLRenderingContext) {
    this.shaderProgram = initShaderProgram(gl, this.vsSource, this.fsSource);
    // Collect all the info needed to use the shader program.
    // Look up which attribute our shader program is using
    // for aVertexPosition and look up uniform locations.
    this.programInfo = {
      program: this.shaderProgram,
      attribLocations: {
        vertexPosition: gl.getAttribLocation(this.shaderProgram, "aVertexPosition"),
      },
      uniformLocations: {
        projectionMatrix: gl.getUniformLocation(this.shaderProgram, "uProjectionMatrix"),
        modelViewMatrix: gl.getUniformLocation(this.shaderProgram, "uModelViewMatrix"),
        color: gl.getUniformLocation(this.shaderProgram, "uColor"),
      },
    };
  }

  public bindPositionsBuffer = (gl: WebGLRenderingContext, positionsBuffer: WebGLBuffer): void => {
    // Tell WebGL how to pull out the positions from the position
    // buffer into the vertexPosition attribute.
    const numComponents = 2;
    const type = gl.FLOAT;
    const normalize = false;
    const stride = 0;
    const offset = 0;
    gl.bindBuffer(gl.ARRAY_BUFFER, positionsBuffer);
    gl.vertexAttribPointer(
      this.programInfo.attribLocations.vertexPosition,
      numComponents,
      type,
      normalize,
      stride,
      offset
    );
    gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition);
  };

  /**
   * useProgram
   * Tell WebGL to use our program when drawing
   * @param gl - WebGLRenderingContext the webgl context where we call webgl functions
   */
  public useProgram = (gl: WebGLRenderingContext): void => {
    gl.useProgram(this.programInfo.program);
  };

  /**
   * setModelViewMatrix
   * Sets the local model matrix in world coordinates
   * @param gl - WebGLRenderingContext the webgl context where we call webgl functions
   */
  public setModelViewMatrix = (gl: WebGLRenderingContext, modelViewMatrix: mat4): void => {
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.modelViewMatrix, false, modelViewMatrix);
  };

  /**
   * setColorUniform
   * sets the color for the selected day block
   * @param gl - WebGLRenderingContext the webgl context where we call webgl functions
   * @param color - the color of the block to be rendered
   */
  public setColorUniform = (gl: WebGLRenderingContext, color: IRGBA): void => {
    gl.uniform4fv(this.programInfo.uniformLocations.color, [color.red, color.green, color.blue, color.alpha]);
  };

  /**
   * draw
   * draws the selected days block so users know they are looking at this day
   * @param gl - WebGLRenderingContext the webgl context where we call webgl functions
   * @param camera - the camera object to transform world coordinates to webgl screen coords
   */
  draw(gl: WebGLRenderingContext, camera: Camera): void {
    const projectionMatrix = camera.getProjectionMatrix(gl);

    // Set the shader uniforms
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.projectionMatrix, false, projectionMatrix);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}
